/**
 * Shared helpers for extracting and displaying customer/client contact details
 * across sales document view/edit modals (Quotes, BillsOfSupply, CreditNotes, etc.)
 */

export const getContactId = (val) => {
  if (val == null || val === '') return '';
  if (typeof val === 'object' && val !== null && 'id' in val) return String(val.id);
  return String(val);
};

export const getCustomerDisplayName = (customer) => {
  const basic = customer?.customer_data?.basic_information || customer?.customer_data || {};
  const candidates = [
    basic.customer_name,
    basic.display_name,
    customer.customer_name,
    customer.customer_display_name,
    customer.name,
    customer.company_name
  ];
  const picked = candidates.find((n) => n && String(n).trim().length > 0);
  return picked || `Customer ${customer?.id ?? ''}`.trim();
};

export const formatAddress = (...parts) => parts
  .flat()
  .filter(Boolean)
  .map((part) => String(part).trim())
  .filter((part) => part.length > 0)
  .join(', ');

export const extractCustomerDetails = (customer, getDisplayName = getCustomerDisplayName) => {
  if (!customer) return { name: '', company: '', address: '', gstin: '', pan: '' };
  const customerData = customer?.customer_data || {};
  const basic = customerData.basic_information || customerData;
  const taxDetails = customerData.tax_details || {};
  const billing = customerData.billing_address || {};
  const structuredAddr = formatAddress(
    billing.address_line_1,
    billing.address_line_2,
    billing.city || customerData.billing_city,
    billing.state || customerData.billing_state,
    billing.postal_code || customerData.billing_postal_code || customerData.billing_pin || customerData.postal_code,
    billing.country || customerData.billing_country,
    billing.address || customerData.billing_address
  );
  const flatAddr = formatAddress(
    customer.address,
    customer.full_address,
    customer.billing_address,
    customer.street_address,
    customer.city,
    customer.state,
    customer.postal_code || customer.pincode,
    customer.country
  );
  return {
    name: getDisplayName(customer),
    company:
      basic.business_name ||
      basic.company_name ||
      customer.customer_company ||
      customer.company_name ||
      customer.company ||
      '',
    address: structuredAddr || flatAddr || '',
    gstin:
      basic.customer_gstin ||
      basic.gstin ||
      customerData.customer_gstin ||
      customerData.gstin ||
      customer.gstin ||
      customer.gst_number ||
      customer.gst_in ||
      '',
    pan: taxDetails.pan || customerData.pan || customer.pan || ''
  };
};

export const extractClientDetails = (client) => {
  if (!client) return { name: '', company: '', address: '', gstin: '', pan: '' };
  const tax = client.tax_details || {};
  const addressPieces = [client.address, client.address_line_1, client.address_line_2, client.city, client.state, client.postal_code || client.pincode, client.country];
  return {
    name: client.name || client.client_name || client.company_name || '',
    company: client.company_name || client.business_name || client.name || '',
    address: formatAddress(addressPieces),
    gstin: client.gst_number || client.gstin || client.gst_in || tax.gstin || '',
    pan: client.pan || tax.pan || ''
  };
};
